import find from './find';

/**
 * Checks if the document has finished loading, and if so, immediately calls the provided callback function. Otherwise, waits for the 'load' event to fire and then calls the callback function.
 * @param {function} callback The callback function to be called when the document finishes loading.
 */
export function documentLoadingCheck(callback) {
  if (document.readyState === 'complete') {
    callback();
  } else {
    window.addEventListener('load', callback);
  }
}

/**
 * Determine whether an element is visually hidden (e.g. .sr-only) based on computed properties.
 * @param {HTMLElement} element The element to check for.
 * @returns {boolean} Returns true if visually hidden based on properties.
 */
export function isScreenReaderOnly(element) {
  const style = getCachedStyle(element);

  // Modern technique: clip-path inset(50%).
  if (style.getPropertyValue('clip-path').startsWith('inset(50%)')) {
    return true;
  }

  // Legacy clipping.
  if (style.clip === 'rect(1px, 1px, 1px, 1px)' || style.clip === 'rect(0px, 0px, 0px, 0px)') {
    return true;
  }

  // Large text-indent offscreen.
  const indent = parseInt(style.textIndent, 10);
  if (!Number.isNaN(indent) && Math.abs(indent) > 5000) {
    return true;
  }

  // Tiny box offscreen.
  if (style.overflow === 'hidden' && parseFloat(style.width) < 2 && parseFloat(style.height) < 2) {
    return true;
  }

  // Absolute positioned far offscreen.
  if (
    style.position === 'absolute' &&
    ['left', 'right', 'top', 'bottom'].some((p) => Math.abs(parseInt(style[p], 10)) > 5000)
  ) {
    return true;
  }

  // Font size 1px or 0px.
  return parseFloat(style.fontSize) < 2;
}

/**
 * Checks if an element is hidden (display: none) based on its attributes and styles.
 * @param {HTMLElement} element The element to check for visibility.
 * @returns {boolean} 'true' if the element is hidden (display: none).
 */
export function isElementHidden(element) {
  if (element.hidden) return true;
  const styles = getCachedStyle(element);
  return (
    styles.getPropertyValue('display') === 'none' ||
    styles.getPropertyValue('visibility') === 'hidden'
  );
}

/**
 * Checks if an element is explicitly hidden from screen readers.
 * Safely handles whitespace and case variations.
 * @param {HTMLElement|Element} $el - The DOM element to evaluate.
 * @returns {boolean} True if aria-hidden is "true".
 */
export function isAriaHidden($el) {
  if (!$el || typeof $el.getAttribute !== 'function') return false;
  return $el.getAttribute('aria-hidden')?.trim().toLowerCase() === 'true';
}

/**
 * Checks if an element's semantics have been neutralized.
 * Handles space-separated role lists (e.g., role="none presentation").
 * @param {HTMLElement|Element} $el - The DOM element to evaluate.
 * @returns {boolean} True if role includes "presentation" or "none".
 */
export function isPresentational($el) {
  if (!$el || typeof $el.getAttribute !== 'function') return false;
  const roleAttr = $el.getAttribute('role');
  if (!roleAttr) return false;
  return roleAttr
    .toLowerCase()
    .split(/\s+/)
    .some((role) => role === 'presentation' || role === 'none');
}

/**
 * Checks if an element is removed from sequential keyboard navigation.
 * Uses the native DOM property for faster, safer numerical evaluation.
 * @param {HTMLElement|Element} $el - The DOM element to evaluate.
 * @returns {boolean} True if tabindex is negative.
 */
export function isNegativeTabindex($el) {
  return $el?.hasAttribute('tabindex') && $el.tabIndex < 0;
}

/**
 * Determines if an element is properly neutralized for both AT and keyboard users.
 * Composes the individual utility functions for clean, reusable logic.
 * @param {HTMLElement|Element} $el - The DOM element to evaluate.
 * @returns {boolean} True if (role is presentation/none OR aria-hidden is true) AND tabindex is negative.
 */
export function isHiddenAndUnfocusable($el) {
  return (isPresentational($el) || isAriaHidden($el)) && isNegativeTabindex($el);
}

/**
 * Determines if an element is disabled natively or via ARIA.
 * @param {HTMLElement|Element} $el - The DOM element to evaluate.
 * @returns {boolean} True if the element has a disabled attribute/property or aria-disabled="true".
 */
export function isDisabled($el) {
  if (!$el || typeof $el.getAttribute !== 'function') return false;
  const isNativeDisabled = $el.hasAttribute('disabled') || $el.disabled === true;
  const isAriaDisabled = $el.getAttribute('aria-disabled')?.trim().toLowerCase() === 'true';
  return isNativeDisabled || isAriaDisabled;
}

/**
 * Checks if an element is invisible in layout.
 * @param {HTMLElement} element The element to check for visibility.
 * @returns {boolean} `true` if the element is visually hidden or hidden, `false` otherwise.
 */
export function isElementVisuallyHiddenOrHidden(element) {
  if (
    (element.offsetWidth === 0 && element.offsetHeight === 0) ||
    (element.clientHeight === 1 && element.clientWidth === 1)
  ) {
    return true;
  }
  return isElementHidden(element);
}

/**
 * Removes ALL non-alphanumeric characters and normalizes whitespace. Accounts for non-Latin characters.
 * @param {string} string - The input text to be sanitized.
 * @returns {string} The sanitized and trimmed string.
 */
export function stripAllSpecialCharacters(string) {
  if (typeof string !== 'string' && typeof string !== 'number') return '';
  return String(string)
    .replace(/[^\p{L}\p{N}\s]/gu, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Sanitize URLs.
 */
const invalidProtocolRegex = /^([^\w]*)(javascript|data|vbscript)/im;
const htmlEntitiesRegex = /&#(\w+)(^\w|;)?/g;
const htmlCtrlEntityRegex = /&(newline|tab);/gi;
const ctrlCharactersRegex =
  // biome-ignore lint/suspicious/noControlCharactersInRegex: original lib.
  /[\u0000-\u001F\u007F-\u009F\u2000-\u200D\uFEFF]/gim;
const urlSchemeRegex = /^.+(:|&colon;)/gim;
const whitespaceEscapeCharsRegex = /(\\|%5[cC])((%(6[eE]|72|74))|[nrt])/g;
const relativeFirstCharacters = ['.', '/'];
const BLANK_URL = 'about:blank';

function isRelativeUrlWithoutProtocol(url) {
  return relativeFirstCharacters.indexOf(url[0]) > -1;
}

function decodeHtmlCharacters(str) {
  const removedNullByte = str.replace(ctrlCharactersRegex, '');
  // biome-ignore lint/correctness/noUnusedFunctionParameters: original lib
  return removedNullByte.replace(htmlEntitiesRegex, (match, dec) => {
    return String.fromCharCode(dec);
  });
}

function isValidUrl(url) {
  if (typeof URL.canParse === 'function') {
    return URL.canParse(url);
  }

  // Fallback for environments without URL.canParse support.
  try {
    const parsedUrl = new URL(url);
    return Boolean(parsedUrl);
  } catch {
    return false;
  }
}

const decodeURIs = (uri) => {
  try {
    return decodeURIComponent(uri);
  } catch {
    return uri;
  }
};

/**
 * Escapes HTML special characters in a string.
 * @param {string} string The string to escape.
 * @returns {string} The escaped string with HTML special characters replaced by their corresponding entities.
 */
export function escapeHTML(input) {
  if (typeof input !== 'string' && typeof input !== 'number') return '';
  const htmlEntities = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;',
    '`': '&#x60;',
  };
  return String(input).replace(/[&<>"'`]/g, (match) => htmlEntities[match]);
}

/**
 * Sanitizes a URL to prevent XSS and ensure valid formatting.
 * MIT License: Copyright (c) 2017 Braintree
 * @link https://github.com/braintree/sanitize-url
 * @param {string} url The URL to sanitize.
 * @returns {string} The safe URL, or about:blank if invalid.
 */
export function sanitizeURL(url) {
  if (!url || typeof url !== 'string') return BLANK_URL;

  // Quick bypass for legitimate base64 data URIs
  const isBase64Data = /^data:([a-z]+\/[a-z0-9-+.]+)?;base64,/i.test(url.trim());
  if (isBase64Data) return url.trim();

  let charsToDecode;
  let decodedUrl = decodeURIs(url.trim());
  do {
    decodedUrl = decodeHtmlCharacters(decodedUrl)
      .replace(htmlCtrlEntityRegex, '')
      .replace(ctrlCharactersRegex, '')
      .replace(whitespaceEscapeCharsRegex, '')
      .trim();
    decodedUrl = decodeURIs(decodedUrl);
    charsToDecode =
      decodedUrl.match(ctrlCharactersRegex) ||
      decodedUrl.match(htmlEntitiesRegex) ||
      decodedUrl.match(htmlCtrlEntityRegex) ||
      decodedUrl.match(whitespaceEscapeCharsRegex);
  } while (charsToDecode && charsToDecode.length > 0);

  const sanitizedUrl = decodedUrl;
  if (!sanitizedUrl) return BLANK_URL;
  if (isRelativeUrlWithoutProtocol(sanitizedUrl)) return sanitizedUrl;

  // Remove any leading whitespace before checking the URL scheme.
  const trimmedUrl = sanitizedUrl.trimStart();
  const urlSchemeParseResults = trimmedUrl.match(urlSchemeRegex);
  if (!urlSchemeParseResults) return sanitizedUrl;
  const urlScheme = urlSchemeParseResults[0].toLowerCase().trim();
  if (invalidProtocolRegex.test(urlScheme)) return BLANK_URL;
  const backSanitized = trimmedUrl.replace(/\\/g, '/');

  // Handle special cases for mailto: and custom deep-link protocols.
  if (urlScheme === 'mailto:' || urlScheme.includes('://')) return backSanitized;

  // For http and https URLs, perform additional validation.
  if (urlScheme === 'http:' || urlScheme === 'https:') {
    if (!isValidUrl(backSanitized)) return BLANK_URL;
    const url = new URL(backSanitized);
    url.protocol = url.protocol.toLowerCase();
    url.hostname = url.hostname.toLowerCase();
    return url.toString();
  }
  return backSanitized;
}

// Sanitize allow list.
const allowedTags = [
  'a',
  'abbr',
  'address',
  'article',
  'aside',
  'audio',
  'b',
  'bdo',
  'blockquote',
  'br',
  'button',
  'canvas',
  'cite',
  'code',
  'data',
  'dd',
  'del',
  'details',
  'dfn',
  'div',
  'dl',
  'dt',
  'em',
  'fieldset',
  'figcaption',
  'figure',
  'footer',
  'form',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'header',
  'hr',
  'i',
  'iframe',
  'img',
  'input',
  'ins',
  'kbd',
  'label',
  'li',
  'main',
  'mark',
  'meter',
  'nav',
  'noscript',
  'ol',
  'output',
  'p',
  'picture',
  'pre',
  'progress',
  'q',
  'rp',
  'rt',
  's',
  'samp',
  'section',
  'select',
  'small',
  'source',
  'span',
  'strong',
  'sub',
  'summary',
  'sup',
  'svg',
  'table',
  'tbody',
  'td',
  'textarea',
  'tfoot',
  'th',
  'thead',
  'time',
  'tr',
  'track',
  'u',
  'ul',
  'var',
  'video',
  'wbr',
  'path',
];
const attrWhitelist = {
  a: ['href', 'title', 'target', 'rel', 'download'],
  img: ['src', 'alt', 'title', 'width', 'height', 'loading', 'srcset', 'sizes'],
  iframe: [
    'src',
    'width',
    'height',
    'title',
    'frameborder',
    'allowfullscreen',
    'loading',
    'sandbox',
  ],
  details: ['open'],
  ol: ['start', 'type', 'reversed'],
  li: ['value'],
  td: ['colspan', 'rowspan'],
  th: ['colspan', 'rowspan', 'scope'],
  global: ['class', 'id', 'role', 'lang', 'dir', 'name'],
  path: ['d', 'fill', 'fill-rule'],
};
/**
 * A lightweight method for sanitizes HTML strings.
 * @param {string} string - The raw HTML string to sanitize.
 * @returns {string} The sanitized HTML string.
 */
export function sanitizeHTML(string) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(string, 'text/html');
  const allElements = doc.body.querySelectorAll('*');
  allElements.forEach((node) => {
    const tag = node.tagName.toLowerCase();
    if (!allowedTags.includes(tag)) {
      node.remove();
      return;
    }
    const allowedForThisTag = attrWhitelist[tag] || [];
    const globals = attrWhitelist.global;
    [...node.attributes].forEach(({ name, value }) => {
      const isAria = name.startsWith('aria-');
      const isAllowed = allowedForThisTag.includes(name) || globals.includes(name) || isAria;
      const isUrlAttr = ['src', 'href', 'srcset'].includes(name);
      if (!isAllowed) {
        node.removeAttribute(name);
      } else if (isUrlAttr) {
        const cleanURL = sanitizeURL(value);
        if (!cleanURL) {
          node.removeAttribute(name);
        } else {
          node.setAttribute(name, cleanURL);
        }
      }
    });
  });
  return doc.body.innerHTML;
}

const baseIgnores = 'noscript,script,style,audio,video,form,iframe';
/**
 * Creates a clone of an element while ignoring specified elements or elements matching a selector.
 * @param {Element} element The element to clone.
 * @param {string[]} [selectors=[]] Optional array of selectors to exclude.
 * @returns {Element|null} The cloned element or null if the root itself is ignored.
 */
export function fnIgnore(element, selectors = []) {
  const ignoreQuery = selectors.length ? `${baseIgnores},${selectors.join(',')}` : baseIgnores;

  // Safety check: if it's not an element, return a clone or null.
  if (!element || element.nodeType !== Node.ELEMENT_NODE) {
    return element ? element.cloneNode(true) : null;
  }

  function cloneTree(node, isRoot = false) {
    const type = node.nodeType;

    if (type === Node.ELEMENT_NODE) {
      // If a CHILD matches, skip it.
      if (node.matches(ignoreQuery) && !isRoot) {
        return null;
      }

      const clone = node.cloneNode(false);

      // If the root was an ignored tag, we return the empty shell.
      if (node.matches(ignoreQuery) && isRoot) {
        return clone;
      }

      let child = node.firstChild;
      while (child) {
        const clonedChild = cloneTree(child);
        if (clonedChild) clone.appendChild(clonedChild);
        child = child.nextSibling;
      }
      return clone;
    }

    if (type === Node.TEXT_NODE) return node.cloneNode(true);
    return null;
  }

  return cloneTree(element, true);
}

/**
 * Retrieves the text content of an HTML element and removes extra whitespaces and line breaks.
 * @param {HTMLElement} element The HTML element to retrieve the text content from.
 * @returns {string} The text content of the HTML element with extra whitespaces and line breaks removed.
 */
let gotText = new WeakMap();
export function getText(element) {
  if (gotText.has(element)) {
    return gotText.get(element);
  }
  const ignore = fnIgnore(element);
  const text = ignore.textContent
    .replace(/[\r\n]+/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  gotText.set(element, text);
  return text;
}

// Garbage collection.
export function resetGetText() {
  gotText = new WeakMap();
}

let styleCaches = {};
/**
 * Retrieves the computed style for a given DOM node and optional pseudo-element.
 * @param {Element|Node|null} node - The DOM element to evaluate.
 * @param {string|null} [pseudoElt=null] - Optional pseudo-element (e.g., ':before').
 * @returns {CSSStyleDeclaration|null} The computed styles.
 */
export const getCachedStyle = (node, pseudoElt = null) => {
  if (!node) return null;

  // Determine the cache key ('base' for standard styles).
  const cacheKey = pseudoElt || 'base';

  // Lazily create a WeakMap for this specific pseudo-element type if needed.
  if (!styleCaches[cacheKey]) {
    styleCaches[cacheKey] = new WeakMap();
  }

  // Fetch or compute the style.
  const targetCache = styleCaches[cacheKey];
  if (!targetCache.has(node)) {
    targetCache.set(node, getComputedStyle(node, pseudoElt));
  }

  return targetCache.get(node);
};

// Garbage collection.
export const resetStyleCache = () => {
  styleCaches = {};
};

let parentCache = new WeakMap();
/**
 * Get cached parent element matching selector.
 * Reduces DOM traversal by caching closest() results.
 * @param {Element} element - The element to search from.
 * @param {string} selector - CSS selector to match.
 * @returns {Element|null} The matching parent or null.
 */
export function getCachedClosest(element, selector) {
  // Note: `instanceof Element` is unavailable across frames.
  if (element?.nodeType !== 1) return null;

  // Validate selector.
  if (typeof selector !== 'string' || selector.trim() === '') return null;

  try {
    // Initialize the per-element cache if missing.
    if (!parentCache.has(element)) {
      parentCache.set(element, new Map());
    }

    const elementCache = parentCache.get(element);
    // Return cached result (including nulls, as they are valid results).
    if (elementCache.has(selector)) {
      return elementCache.get(selector);
    }

    // Execute and cache.
    const result = element.closest(selector);
    elementCache.set(selector, result);
    return result;
  } catch {
    return null;
  }
}

// Garbage collection.
export function resetParentCache() {
  parentCache = new WeakMap();
}

/**
 * Removes extra whitespaces and line breaks from a string.
 * @param {string} string The string.
 * @returns {string} String with line breaks and extra white space removed.
 */
export function removeWhitespace(string) {
  if (typeof string !== 'string' && typeof string !== 'number') return '';
  return String(string).replace(/\s+/g, ' ').trim();
}

/**
 * Removes non-printable ASCII control characters and junk icons from a string, and removes extra whitespace.
 * @param {string} string - The input string to clean.
 * @returns {string} The string with control characters removed.
 */
export function normalizeString(string) {
  if (typeof string !== 'string' && typeof string !== 'number') return '';
  // biome-ignore lint/suspicious/noControlCharactersInRegex: Strip junk icons/PUA characters.
  return removeWhitespace(String(string).replace(/[\u0000-\u001F\u007F-\u009F]/g, ''));
}

/**
 * Truncate string.
 * @param {*} string The string to truncate.
 * @param {*} maxLength Desired max length of string.
 * @returns Truncated string.
 */
export function truncateString(string, maxLength) {
  if (typeof string !== 'string' && typeof string !== 'number') return '';
  const truncatedString = String(string).substring(0, maxLength).trimEnd();
  return string.length > maxLength ? `${truncatedString}...` : String(string);
}

/**
 * Debounces a callback function, ensuring it is only executed after a certain wait period
 * has passed since the last invocation.
 * @param {function} callback The callback function to debounce.
 * @param {number} wait The wait period in milliseconds before the callback function is executed.
 * @returns {function} The debounced function.
 * @link https://www.joshwcomeau.com/snippets/javascript/debounce/
 */
export function debounce(callback, wait) {
  let timeoutId = null;
  return (...args) => {
    window.clearTimeout(timeoutId);
    timeoutId = window.setTimeout(() => {
      callback(...args);
    }, wait);
  };
}

/**
 * Finds the visible parent of an element that matches a given CSS property and value.
 * @param {Element} element The element for which the visible parent needs to be found.
 * @param {string} property The CSS property to match against.
 * @param {string} value The value of the CSS property to match against.
 * @returns {Element|null} The visible parent element that matches the given property and value, or null if not found.
 */
export function findVisibleParent(element, property, value) {
  let $el = element;
  while ($el) {
    const style = getCachedStyle($el);
    const propValue = style.getPropertyValue(property);
    if (propValue === value) {
      return $el;
    }
    $el = $el.parentElement;
  }
  return null;
}

/**
 * Calculates the offset top of an element relative to the viewport.
 * @param {Element} element The element for which the offset top needs to be calculated.
 * @returns {Object} An object with a `top` property that represents the offset top of the element relative to the viewport.
 */
export function offsetTop(element) {
  const rect = element.getBoundingClientRect();
  const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
  return { top: rect.top + scrollTop };
}

/**
 * A utility object for handling storage operations using localStorage and sessionStorage.
 * @param  {String} key
 * @param  {string} value
 * @return {String} Return key.
 */
export const store = {
  getItem(key) {
    try {
      if (localStorage.getItem(key) === null) {
        return sessionStorage.getItem(key);
      }
      return localStorage.getItem(key);
    } catch {
      // Cookies totally disabled.
      return false;
    }
  },
  setItem(key, value) {
    try {
      localStorage.setItem(key, value);
    } catch {
      sessionStorage.setItem(key, value);
    }
    return true;
  },
  removeItem(key) {
    try {
      localStorage.removeItem(key);
    } catch {
      sessionStorage.removeItem(key);
    }
    return true;
  },
};

/**
 * Adds a pulsing border effect to an element for 2.5 seconds.
 * @param {Element} element The element to which the pulsing border effect needs to be added.
 */
export function addPulse(element) {
  const border = 'data-sa11y-pulse-border';
  element.setAttribute(border, '');
  setTimeout(() => element.removeAttribute(border), 2500);
}

/**
 * Gets the next sibling element that matches the given selector, or the next sibling element if no selector is provided.
 * @param {HTMLElement} element The DOM element whose next sibling to retrieve.
 * @param {string} selector The optional selector to filter the next siblings. If not provided, the next sibling element will be returned regardless of its type.
 * @returns {HTMLElement|string} The next sibling element that matches the given selector, or the next sibling element if no selector is provided. If no matching sibling is found, an empty string is returned.
 */
export function getNextSibling(element, selector) {
  let sibling = element.nextElementSibling;
  if (!selector) {
    return sibling;
  }
  while (sibling) {
    if (sibling.matches(selector)) {
      return sibling;
    }
    sibling = sibling.nextElementSibling;
  }
  return '';
}

/**
 * Generates a unique key for dismissing items.
 * @param {string} string The string to be prepared for dismissal (without special chars).
 * @returns {string} The truncated string with a maximum of 256 characters.
 */
export function prepareDismissal(string) {
  return String(string)
    .replace(/([^0-9a-zA-Z])/g, '')
    .substring(0, 256);
}

/**
 * Generates a selector path for the given DOM element.
 * @param {Element} element The DOM element for which to generate the selector path.
 * @returns {string} The selector path as a string.
 * @link https://www.geeksforgeeks.org/how-to-create-a-function-generateselector-to-generate-css-selector-path-of-a-dom-element/
 * @link https://dev.to/aniket_chauhan/generate-a-css-selector-path-of-a-dom-element-4aim
 */
export function generateSelectorPath(element) {
  const path = [];
  let currentElement = element;
  while (currentElement) {
    let selector = currentElement.localName;
    if (currentElement.id) {
      selector += `#${currentElement.id}`;
      path.unshift(selector);
      break;
    } else {
      const classAttr = currentElement.getAttribute ? currentElement.getAttribute('class') : null;
      if (classAttr) {
        const classSelector = classAttr.trim().replace(/\s+/g, '.');
        if (classSelector) {
          selector += `.${classSelector}`;
        }
      }
    }

    const parentElement = currentElement.parentNode;
    if (parentElement) {
      const siblings = parentElement.children;
      if (siblings.length > 1) {
        const index = Array.prototype.indexOf.call(siblings, currentElement) + 1;
        selector += `:nth-child(${index})`;
      }
      path.unshift(selector);
    } else {
      break;
    }
    currentElement = currentElement.parentNode.host || currentElement.parentNode;
  }
  return path.join(' > ');
}

/**
 * Traps focus within an element by looping focus back to the beginning or end
 * when the Tab key is pressed.
 * @param {Element} element The DOM element to trap focus within.
 * @author Hidde de Vries
 * @link https://hidde.blog/using-javascript-to-trap-focus-in-an-element/
 */
export function trapFocus(element) {
  const focusable = element.querySelectorAll(
    'a[href]:not([disabled]), button:not([disabled]), input[type="color"]',
  );
  const firstFocusable = focusable[0];
  const lastFocusable = focusable[focusable.length - 1];
  element.addEventListener('keydown', (e) => {
    const isTabPressed = e.key === 'Tab' || e.keyCode === 9;

    // "document.activeElement" does not work within ShadowDOM.
    const root = element.getRootNode();

    if (!isTabPressed) {
      return;
    }
    if (e.shiftKey) {
      if (root.activeElement === firstFocusable) {
        lastFocusable.focus();
        e.preventDefault();
      }
    } else if (root.activeElement === lastFocusable) {
      firstFocusable.focus();
      e.preventDefault();
    }
  });
}

/**
 * Finds all data-attributes specified in array, and removes them from the document.
 * @param {Array<string>} attributes The array of data-attributes to be reset.
 * @param {string} root The root element to search for elements (optional, defaults to 'document').
 * @returns {void}
 */
export function resetAttributes(attributes, root) {
  attributes.forEach((attr) => {
    const reset = find(`[${attr}]`, `${root}`);
    reset.forEach(($el) => {
      $el.removeAttribute(attr);
    });
  });
}

/**
 * Removes the specified elements from the document.
 * @param {string} root The root element to search for elements (optional, defaults to 'document').
 * @returns {void}
 */
export function remove(elements, root) {
  const allElements = find(`${elements}`, `${root}`);
  allElements.forEach(($el) => {
    $el?.parentNode?.removeChild($el);
  });
}

/**
 * Checks if a scrollable area within a container element is scrollable or not, and applies appropriate CSS classes and attributes. Make sure to add aria-label manually.
 * @param {Element} scrollArea The scrollable area element to check.
 * @param {Element} container The container element that wraps the scrollable area.
 * @param {Attribute} ariaLabel Give scroll area an accessible name and region landmark.
 */
export function isScrollable(scrollArea, container, ariaLabel) {
  setTimeout(() => {
    if (scrollArea.scrollHeight > container.clientHeight) {
      container.classList.add('scrollable');
      scrollArea.setAttribute('tabindex', '0');
      if (ariaLabel) {
        scrollArea.setAttribute('aria-label', ariaLabel);
        scrollArea.setAttribute('role', 'region');
      }
    } else {
      container.classList.remove('scrollable');
    }
  }, 50);
}

/**
 * Get the best image source from an element, considering data-src, srcset, and src attributes.
 * @param {HTMLElement} element - The image element to extract the source from.
 * @returns {string} - The best available source URL.
 */
export function getBestImageSource(element) {
  const getLastSrc = (src) => src?.split(/,\s+/).pop()?.trim()?.split(/\s+/)[0];

  // Return absolute URLs. Necessary for HTML export.
  const resolveUrl = (src) => (src ? new URL(src, window.location.href).href : null);

  // Check data-src or srcset.
  const dataSrc = getLastSrc(element.getAttribute('data-src') || element.getAttribute('srcset'));
  if (dataSrc) return resolveUrl(dataSrc);

  // Check <picture> sources.
  const pictureSrcset = getCachedClosest(element, 'picture')
    ?.querySelector('source[srcset]')
    ?.getAttribute('srcset');
  const pictureSrc = getLastSrc(pictureSrcset);
  if (pictureSrc) return resolveUrl(pictureSrc);

  return resolveUrl(element.getAttribute('src'));
}

/**
 * Converts a Blob object to a Base64-encoded string.
 * @param {Blob} blob - The Blob object to convert.
 * @returns {Promise<string>} A promise that resolves to a Base64 string representation of the Blob.
 */
export const blobToBase64 = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      let { result } = reader;
      // Ensure the correct MIME type if it's missing or wrong. Necessary for uncommon image formats.
      const detectedMime = blob?.type?.startsWith('image/') ? blob.type : 'image/png'; // Default fallback
      if (result.startsWith('data:application/octet-stream')) {
        result = result.replace('data:application/octet-stream', `data:${detectedMime}`);
      }
      resolve(result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

/**
 * Generate a DOM node preview for an issue.
 * @param {Object} issueObject The issue object.
 * @param {boolean} convertBase64 Optional. Convert image to Base64.
 * @returns {HTMLElement|Promise<HTMLElement>} Returns a DOM Node or a Promise resolving to one.
 */
export function generateElementPreview(issueObject, convertBase64 = false) {
  const issueElement = issueObject.element;

  const createCodeFallback = () => {
    const pre = document.createElement('pre');
    const code = document.createElement('code');
    code.textContent = truncateString(issueObject.htmlPath, 150);
    pre.appendChild(code);
    return pre;
  };

  const tagHandlers = {
    A: (element) => {
      const text = getText(element);
      if (text.length > 1 && element.href && !element.hasAttribute('role')) {
        const anchor = document.createElement('a');
        anchor.href = sanitizeURL(element.href);
        anchor.textContent = truncateString(text, 150);
        return anchor;
      }
      return createCodeFallback();
    },
    IMG: (element) => {
      const src = getBestImageSource(element);
      if (!src) return createCodeFallback();

      const containerAnchor = getCachedClosest(element, 'a[href]');

      const buildImgElement = (url) => {
        const img = document.createElement('img');
        img.src = url.startsWith('data:image/') ? url : sanitizeURL(url);
        if (element.alt) img.alt = element.alt;

        if (containerAnchor) {
          const a = document.createElement('a');
          a.href = sanitizeURL(containerAnchor.href);
          a.rel = 'noopener noreferrer';
          a.appendChild(img);
          return a;
        }
        return img;
      };

      if (!convertBase64) return buildImgElement(src);

      return (async () => {
        try {
          if (new URL(src, window.location.origin).origin !== window.location.origin)
            throw new Error();
          const response = await fetch(src);
          const blob = await response.blob();
          const b64 = await blobToBase64(blob);
          return buildImgElement(b64);
        } catch {
          return buildImgElement(src);
        }
      })();
    },
  };

  const handler = tagHandlers[issueElement.tagName];
  return handler ? handler(issueElement) : createCodeFallback();
}

/**
 * Check if an element's visible text is included in the accessible name.
 * To minimize false positives: iterate through all child nodes of the element, checking for visibility.
 * @param {element} $el The element to test.
 * @param {string} accName The computed accessible name of the element.
 * @param {Array} exclusions Array of exclusions.
 * @param {Array} linkIgnoreStrings Array of string exclusions.
 * @returns {boolean}
 */
export function isVisibleTextInAccName($el, accName, exclusions = [], linkIgnoreStrings) {
  let text = '';

  // Prep exclusions.
  const excludeSelector = exclusions?.length ? exclusions.join(',') : '';
  const ignoreStrings = Array.isArray(linkIgnoreStrings) ? linkIgnoreStrings : null;
  const stripIgnored = (value = '') =>
    ignoreStrings ? ignoreStrings.reduce((result, str) => result.replace(str, ''), value) : value;

  // Iterate though each child node.
  $el.childNodes.forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      text += stripIgnored(node.textContent);
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
      return;
    }

    // Exclusions based on matched selectors.
    if (excludeSelector && node.matches(excludeSelector)) {
      return;
    }

    // Only return text content if it's not hidden.
    if (!isElementVisuallyHiddenOrHidden(node)) {
      text += stripIgnored(getText(node));
    }
  });

  // Ignore emojis.
  const emojiRegex = /[\p{Emoji_Presentation}\p{Extended_Pictographic}]/gu;
  let visibleText = text.replace(emojiRegex, '');

  // Final visible text.
  visibleText = removeWhitespace(visibleText).toLowerCase();

  // If visible text is just an x character, ignore.
  if (/^[x×✕✖✗✘]$/i.test(visibleText)) {
    return false;
  }

  // Check if visible text is included in accessible name.
  return visibleText.length !== 0 && !accName.toLowerCase().includes(visibleText);
}

/**
 * Standardize the href attribute of a link by removing any trailing slashes and stripping the protocol (http, https) and 'www.' prefix. Used to minimize false positives for link check module.
 * @param {HTMLElement} $el - The element from which to retrieve the href attribute.
 * @returns {string} - The standardized href.
 */
export function standardizeHref($el) {
  let href = $el.getAttribute('href');
  href = removeWhitespace(href).toLowerCase();

  // Remove trailing slash if it exists.
  if (href.endsWith('/')) {
    href = href.slice(0, -1);
  }

  // Remove protocol and www., without affecting subdomains.
  href = href.replace(/^https?:\/\/(www\.)?/, '');

  // Remove common file extensions at the end.
  href = href.replace(/\.(html|php|htm|asp|aspx)$/i, '');

  return href;
}

/**
 * Roving tabindex menu for page outline.
 * Thanks to Srijan for this snippet!
 * @param {HTMLElement} container - Parent element (e.g., ul, div).
 * @param {HTMLElement[]} children - Focusable child elements inside container.
 */
export function initRovingTabindex(container, children) {
  let current = 0;
  const handleKeyDown = (e) => {
    if (!['ArrowUp', 'ArrowDown', 'Space'].includes(e.code)) {
      return;
    }
    if (e.code === 'Space') {
      children[current].click();
      e.preventDefault();
      return;
    }
    const selected = children[current];
    selected.setAttribute('tabindex', -1);
    let next;
    if (e.code === 'ArrowDown') {
      next = current + 1;
      if (current === children.length - 1) {
        next = 0;
      }
    } else if (e.code === 'ArrowUp') {
      next = current - 1;
      if (current === 0) {
        next = children.length - 1;
      }
    }
    children[next].setAttribute('tabindex', 0);
    children[next].focus();
    current = next;
    e.preventDefault();
  };

  container.addEventListener('focus', () => {
    if (children.length > 0) {
      container.setAttribute('tabindex', -1);
      children[current].setAttribute('tabindex', 0);
      children[current].focus();
    }
    container.addEventListener('keydown', handleKeyDown);
  });

  container.addEventListener('blur', () => {
    container.removeEventListener('keydown', handleKeyDown);
  });
}

/**
 * Detects if the browser supports CSS Anchor Positioning.
 * @link https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_anchor_positioning
 */
export function supportsAnchorPositioning() {
  return CSS.supports('anchor-name: --sa11y') && CSS.supports('position-anchor: --sa11y');
}

/**
 * Generates a Regex pattern from an array OR a comma-separated string. Safely escapes all special characters.
 * @param {string[]|string} input - Array of strings (e.g. ['(External)']) OR string (e.g. "(External), [draft]").
 * @param {boolean} [exactMatch=false] - If true, wraps each pattern in word boundaries (\b) for whole-word matching.
 * @returns {RegExp|null} The compiled Regex object, or null if input is empty/invalid.
 */
export function generateRegexString(input, matchStart = false) {
  if (!input) return null;

  // Return RegExp directly.
  if (input instanceof RegExp) return input;

  // Normalize input.
  let patterns = [];
  if (Array.isArray(input)) {
    patterns = input;
  } else if (typeof input === 'string') {
    patterns = input.split(',').map((s) => s.trim());
  } else {
    return null;
  }

  // Filter out empty entries to prevent matching 'everything' (empty regex issue).
  patterns = patterns.filter((p) => p && p.length > 0);
  if (patterns.length === 0) return null;

  // Helper to escape special regex characters.
  const escapeRegExp = (string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  };

  // Build the pattern string.
  const joinedPatterns = patterns.map(escapeRegExp).join('|');

  // If matchStart is true, wrap in ^(?: ... ) to anchor the entire group to the start.
  const finalPattern = matchStart ? `^(?:${joinedPatterns})` : joinedPatterns;

  // Compile final case-insensitive regex.
  return new RegExp(finalPattern, 'i');
}

/**
 * Generates a SHA-256 hex digest from a pepper + message combination, used for dismiss keys within localstorage. Yoinked from Editoria11y!
 * @async
 * @param {string} pepper - A secret or application-specific salt.
 * @param {string} message - The message or identifier to hash.
 * @returns {Promise<string>} A lowercase hexadecimal SHA-256 digest.
 */
export async function dismissDigest(pepper, message) {
  const msgUint8 = new TextEncoder().encode(pepper + message);
  const hashBuffer = await window.crypto.subtle.digest('SHA-256', msgUint8);
  if (Uint8Array.prototype.toHex) {
    return new Uint8Array(hashBuffer).toHex();
  }
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
}

let langCache;
/**
 * Validates BCP 47 language code using Intl API with safe handling.
 * @param {string} code
 * @returns {{valid: boolean, suggest?: string}}
 */
export function validateLang(code, displayLangCode) {
  if (typeof code !== 'string') return { valid: false };
  const norm = code.trim().replace(/_/g, '-');
  if (!langCache && typeof Intl !== 'undefined') {
    try {
      langCache = new Intl.DisplayNames([displayLangCode], { type: 'language', fallback: 'none' });
    } catch {}
  }

  if (langCache) {
    // Safe helper to catch RangeErrors from invalid structures (e.g., "123")
    const check = (val) => {
      try {
        return langCache.of(val);
      } catch {
        return false;
      }
    };
    if (check(code)) return { valid: true };
    if (check(norm)) return { valid: false, suggest: norm };
    return { valid: false };
  }

  // Regex fallback for pre-2021 browsers.
  return { valid: /^[a-z]{2,3}(-[a-z]{4})?(-[a-z]{2,4})?$/i.test(norm) };
}
